import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { useAbac } from 'react-abac';

import { Settings, SettingsOutlined } from '@mui/icons-material';

import FolderIcon from '@mui/icons-material/Folder';

import FolderOpenIcon from '@mui/icons-material/FolderOpen';

import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import { colors } from 'theme/colors';

import NewSideBar from 'ui/NewSideBar';
import { ProductType } from 'ui/NewSideBar/types';

import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import { EntityModules, UserRole } from 'core/constants';

import { canAccessRisksProduct } from 'utils/roles/permissions';
import { ADMIN_AND_ACCOUNT_MANAGERS, ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import { LONE_WORKING_REPORTING_DASHBOARD_TYPES } from 'containers/LoneWorkingReporting/constants';

import { getVisibleCategories } from 'stores/KnockPreferences/config';

import { ActiveHomeIcon, HomeIcon } from './Icons/HomeIcon';
import { ActiveAllInboxIcon, ActiveInboxIcon, AllInboxIcon, InboxIcon } from './Icons/InboxIcon';
import { ActiveListIcon, ListIcon } from './Icons/ListIcon';
import { ActionIcon, ActiveActionIcon } from './Icons/ActionsIcon';
import { ActiveProtectorIcon, ProtectorIcon } from './Icons/ProtectorIcon';
import { ActiveAuditsIcon, AuditsIcon } from './Icons/AuditsIcon';
import { ActiveAnalyticsIcon, AnalyticsIcon } from './Icons/AnalyticsIcon';
import { ActiveManageIcon, ManageIcon } from './Icons/ManageIcon';
import { ActiveDevicesIcon, DevicesIcon } from './Icons/DevicesIcon';
import { ActiveUsageIcon, UsageIcon } from './Icons/UsageIcon';
import { ActiveARPIcon, ARPIcon } from './Icons/ARPIcon';
import { ActiveFormIcon, FormIcon } from './Icons/FromsIcon';
import { ActiveFormTemplateIcon, FormTemplateIcon } from './Icons/FormTemplatesIcon';
import { SubItemsType } from './types';
import { ActiveMoreAppsIcon, MoreAppsIcon } from './Icons/MoreAppsIcon';
import { ActiveMapIcon, MapIcon } from './Icons/MapIcon';
import { SideBarPermissions } from './permissions/types';
import { ActiveSitesIcon, SitesIcon } from './Icons/SitesIcon';
import { ActiveAssetsIcon, AssetsIcon } from './Icons/AssetsIcon';
import { ActiveRiskIcon, RiskIcon } from './Icons/RiskIcon';
import { ActiveContactsIcon, ContactsIcon } from './Icons/ContactsIcon';
import {
  buildObjectNode,
  resolveDefaultSettingsPath,
  CONFIGURABLE_OBJECT_MODULES,
  settingsPathMatches,
  canSeeOrganisationSettings,
  hasNotificationsLicence,
} from './utils';
import { ActiveIncidentIcon, IncidentIcon } from './Icons/IncidentsIcon';
import { ActiveSchedulesIcon, SchedulesIcon } from './Icons/SchedulesIcon';
import { FolderNavigation } from './components';
import { risksProduct } from './risks/risksProduct';
import SettingsNavTree, { SettingsNavNode } from './components/SettingsNavTree';

const SideBar = (): React.ReactElement => {
  const {
    routing,
    session: { user, hasOrganisationLogo, loadOrganisationLogo },
    ui: { subSideBarOpen },
    dualInbox,
    folders,
  } = useStore();
  const licenses = user ? user.licenses : {};
  const { userHasPermissions } = useAbac();

  React.useEffect(() => {
    loadOrganisationLogo();
  }, [hasOrganisationLogo]);

  React.useEffect(() => {
    if (dualInbox.initialSync === undefined) {
      void dualInbox.loadInitialData();
    }
  }, []);

  React.useEffect(() => {
    if (user?.activeEntityModules.includes(EntityModules.Documents) && !folders.isLoaded && !folders.isLoading) {
      folders.loadFolders();
    }
  }, [user?.activeEntityModules]);

  const Products: ProductType[] = [];

  if (userHasPermissions(SideBarPermissions.HOME_PAGE_VISIBLE)) {
    Products.push({
      name: 'Home',
      Icon: <HomeIcon />,
      ActiveIcon: <ActiveHomeIcon />,
      to: routes.dashboard.home.toString(),
      isSelected: routing.location.pathname === routes.dashboard.home.toString(),
    });
  }

  if (userHasPermissions(SideBarPermissions.INBOX_VISIBLE, licenses)) {
    const inboxRoot = routes.dashboard.notifications.toString();
    const inboxSubItems: SubItemsType = [
      {
        name: 'All',
        Icon: <AllInboxIcon />,
        ActiveIcon: <ActiveAllInboxIcon />,
        to: inboxRoot,
        isSelected: routing.location.pathname === inboxRoot,
      },
    ];

    // Category sub-items are Knock-only — legacy notifications have no category metadata
    // and are always rendered under "All". Hide these entries when Knock is disabled.
    if (dualInbox.knockEnabled && user) {
      // Same licence/role rules as the Knock preference centre, so the Inbox nav and the
      // notification settings page stay consistent.
      const visibleCategoryKeys = new Set(
        getVisibleCategories(user.licenses, user.activeEntityModules).map((category) => category.key)
      );

      const categoryItems: { key: string; item: SubItemsType[number] }[] = [
        {
          key: 'events',
          item: {
            name: 'Events',
            Icon: <IncidentIcon />,
            ActiveIcon: <ActiveIncidentIcon />,
            to: reverse(routes.dashboard.notifications.category, { category: 'events' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'events' })
            ),
          },
        },
        {
          key: 'actions',
          item: {
            name: 'Actions',
            Icon: <ActionIcon />,
            ActiveIcon: <ActiveActionIcon />,
            to: reverse(routes.dashboard.notifications.category, { category: 'actions' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'actions' })
            ),
          },
        },
        {
          key: 'audits',
          item: {
            name: 'Audits',
            Icon: <AuditsIcon />,
            ActiveIcon: <ActiveAuditsIcon />,
            to: reverse(routes.dashboard.notifications.category, { category: 'audits' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'audits' })
            ),
          },
        },
        {
          key: 'sites',
          item: {
            name: 'Sites',
            Icon: <SitesIcon />,
            ActiveIcon: <ActiveSitesIcon />,
            to: reverse(routes.dashboard.notifications.category, { category: 'sites' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'sites' })
            ),
          },
        },
        {
          key: 'assets',
          item: {
            name: 'Assets',
            Icon: <AssetsIcon />,
            ActiveIcon: <ActiveAssetsIcon />,
            to: reverse(routes.dashboard.notifications.category, { category: 'assets' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'assets' })
            ),
          },
        },
        {
          key: 'documents',
          item: {
            name: 'Documents',
            Icon: <FolderOpenIcon sx={{ color: colors.ui.border.light, width: '22px', height: '22px' }} />,
            ActiveIcon: <FolderIcon sx={{ color: colors.common.white, width: '22px', height: '22px' }} />,
            to: reverse(routes.dashboard.notifications.category, { category: 'documents' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'documents' })
            ),
          },
        },
        {
          key: 'risks',
          item: {
            name: 'Risks',
            Icon: <RiskIcon />,
            ActiveIcon: <ActiveRiskIcon />,
            to: reverse(routes.dashboard.notifications.category, { category: 'risks' }),
            isSelected: routing.location.pathname.startsWith(
              reverse(routes.dashboard.notifications.category, { category: 'risks' })
            ),
          },
        },
      ];

      categoryItems.forEach(({ key, item }) => {
        if (visibleCategoryKeys.has(key)) {
          inboxSubItems.push(item);
        }
      });
    }

    Products.push({
      to: inboxRoot,
      isSelected: routing.location.pathname.startsWith(inboxRoot),
      name: 'Inbox',
      Icon: <InboxIcon notDismissedNotificationsCount={dualInbox.notDismissedNotificationsCount} />,
      ActiveIcon: <ActiveInboxIcon notDismissedNotificationsCount={dualInbox.notDismissedNotificationsCount} />,
      subItems: inboxSubItems,
    });
  }

  if (userHasPermissions(SideBarPermissions.ACTIONS_VISIBLE, licenses)) {
    const hasAuditsLicense = (user?.licenses?.workflows && user?.scheduleEnabled) ?? false;
    const hasAcknowledgements = (user?.documentAcknowledgementsEnabled && user?.licenses?.documents) ?? false;

    // The recipient acknowledgement page lives under Actions (breadcrumb + "Back to Actions"),
    // so treat /acknowledgements/ as part of the Actions context for sidebar highlighting.
    const inActionsContext =
      routing.location.pathname.startsWith(routes.dashboard.tasks.toString()) ||
      routing.location.pathname.startsWith(routes.dashboard.acknowledgements.toString());

    const actionsSubItems: SubItemsType = [
      {
        name: 'Tasks',
        Icon: <ListIcon />,
        ActiveIcon: <ActiveListIcon />,
        to: routes.dashboard.tasks.list.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.tasks.list.toString()),
      },
    ];

    if (hasAuditsLicense) {
      actionsSubItems.push({
        name: 'Audits',
        Icon: <AuditsIcon />,
        ActiveIcon: <ActiveAuditsIcon />,
        to: routes.dashboard.tasks.audits.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.tasks.audits.toString()),
      });
    }

    if (hasAcknowledgements) {
      actionsSubItems.push({
        name: 'Acknowledgements',
        Icon: <VerifiedUserOutlinedIcon sx={{ color: colors.ui.border.light, width: '18px', height: '18px' }} />,
        ActiveIcon: <VerifiedUserOutlinedIcon sx={{ color: colors.common.white, width: '18px', height: '18px' }} />,
        to: routes.dashboard.tasks.acknowledgements.toString(),
        // The recipient page (/acknowledgements/<uuid>/) is the active Acknowledgements surface too.
        isSelected:
          routing.location.pathname.startsWith(routes.dashboard.tasks.acknowledgements.toString()) ||
          routing.location.pathname.startsWith(routes.dashboard.acknowledgements.toString()),
      });
    }

    if (canAccessRisksProduct(user)) {
      actionsSubItems.push({
        name: 'Risks',
        Icon: <RiskIcon />,
        ActiveIcon: <ActiveRiskIcon />,
        to: routes.dashboard.tasks.risks.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.tasks.risks.toString()),
        testId: 'sidebar-actions-risks',
      });
    }

    Products.push({
      name: 'Actions',
      Icon: <ActionIcon />,
      ActiveIcon: <ActiveActionIcon />,
      to: routes.dashboard.tasks.list.toString(),
      isSelected: inActionsContext,
      subItems: actionsSubItems,
    });
  }

  // Schedules — relocated inside the Audits entity nav when the audits-as-entity flag is on
  if (
    user?.scheduleEnabled &&
    !user.activeEntityModules.includes(EntityModules.Audits) &&
    ADMIN_AND_MANAGERS.includes(user.role) &&
    userHasPermissions(SideBarPermissions.AUDITS_VISIBLE, licenses)
  ) {
    Products.push({
      name: 'Schedules',
      Icon: <SchedulesIcon />,
      ActiveIcon: <ActiveSchedulesIcon />,
      to: routes.dashboard.schedules.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.schedules.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.schedules.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.schedules.toString()),
        },
      ],
    });
  }

  // Events
  if (user && user.activeEntityModules.includes(EntityModules.Events)) {
    Products.push({
      name: 'Events',
      Icon: <IncidentIcon />,
      ActiveIcon: <ActiveIncidentIcon />,
      to: routes.dashboard.events.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.events.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.events.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.events.toString()),
        },
      ],
    });
  }

  // Audits, flag on: replaces the legacy nav and hosts Schedules. Object Manager is suppressed by
  // omission — Audits is never added to the OM entityElements array below (Documents treatment).
  if (user && user.activeEntityModules.includes(EntityModules.Audits)) {
    const subItems: SubItemsType = [
      {
        name: 'Overview',
        Icon: <FormIcon />,
        ActiveIcon: <ActiveFormIcon />,
        to: routes.dashboard.audits.forms.toString(),
        isSelected:
          routing.location.pathname.startsWith(routes.dashboard.audits.forms.toString()) ||
          routing.location.pathname.startsWith(`${routes.dashboard.audits.toString()}related/`),
      },
    ];

    if (ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)) {
      subItems.push({
        name: 'Templates',
        Icon: <FormTemplateIcon />,
        ActiveIcon: <ActiveFormTemplateIcon />,
        to: routes.dashboard.audits.templates.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.audits.templates.toString()),
      });
    }

    if (user.scheduleEnabled && ADMIN_AND_MANAGERS.includes(user.role)) {
      subItems.push({
        name: 'Schedules',
        Icon: <SchedulesIcon />,
        ActiveIcon: <ActiveSchedulesIcon />,
        to: routes.dashboard.schedules.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.schedules.toString()),
      });
    }

    Products.push({
      name: 'Audits',
      Icon: <AuditsIcon />,
      ActiveIcon: <ActiveAuditsIcon />,
      to: routes.dashboard.audits.forms.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.audits.toString()),
      subItems,
    });
  }

  // Audits (legacy workflows-api nav — audits-as-entity flag off)
  else if (userHasPermissions(SideBarPermissions.AUDITS_VISIBLE, licenses)) {
    const subItems: SubItemsType = [
      {
        name: 'Forms',
        Icon: <FormIcon />,
        ActiveIcon: <ActiveFormIcon />,
        to: routes.dashboard.audits.forms.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.audits.forms.toString()),
      },
    ];

    if (user && ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)) {
      subItems.push({
        name: 'Form Templates',
        Icon: <FormTemplateIcon />,
        ActiveIcon: <ActiveFormTemplateIcon />,
        to: routes.dashboard.audits.templates.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.audits.templates.toString()),
      });
    }

    Products.push({
      name: 'Audits',
      Icon: <AuditsIcon />,
      ActiveIcon: <ActiveAuditsIcon />,
      to: routes.dashboard.audits.forms.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.audits.toString()),
      subItems,
    });
  }

  // Documents
  if (user && user.activeEntityModules.includes(EntityModules.Documents)) {
    Products.push({
      name: undefined,
      tooltipTitle: 'Documents',
      Icon: <FolderOpenIcon sx={{ color: '#C0C7D3', width: '22px', height: '22px' }} />,
      ActiveIcon: <FolderIcon sx={{ color: colors.common.white, width: '22px', height: '22px' }} />,
      to: routes.dashboard.documents.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.documents.toString()),
      customContent: <FolderNavigation />,
    });
  }

  // Lone Working
  if (userHasPermissions(SideBarPermissions.LONE_WORKING_VISIBLE, licenses)) {
    const LoneWorkingSubItems: SubItemsType = [
      {
        name: 'Overview',
        Icon: <ListIcon />,
        ActiveIcon: <ActiveListIcon />,
        to: routes.dashboard.alarms.red.toString(),
        isSelected:
          routing.location.pathname.startsWith(routes.dashboard.alarms.red.toString()) ||
          routing.location.pathname.startsWith(routes.dashboard.alarms.amber.toString()),
      },
    ];

    if (userHasPermissions(SideBarPermissions.LONE_WORKING_MANAGE_VISIBLE)) {
      LoneWorkingSubItems.push({
        name: 'Manage',
        Icon: <ManageIcon />,
        ActiveIcon: <ActiveManageIcon />,
        to: routes.dashboard.alarms.manage.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.manage.toString()),
      });
    }

    if (userHasPermissions(SideBarPermissions.LONE_WORKING_DEVICES_VISIBLE)) {
      LoneWorkingSubItems.push({
        name: 'Devices',
        Icon: <DevicesIcon />,
        ActiveIcon: <ActiveDevicesIcon />,
        to: routes.dashboard.alarms.devices.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.devices.toString()),
      });
    }

    if (userHasPermissions(SideBarPermissions.LONE_WORKING_USAGE_VISIBLE)) {
      if (user?.loneWorkingAnalyticsEnabled && ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)) {
        const loneWorkingReportingDashboards = user?.dashboards?.loneWorkingReporting;

        if (loneWorkingReportingDashboards && Object.keys(loneWorkingReportingDashboards).length > 0) {
          const reportingSubItems = LONE_WORKING_REPORTING_DASHBOARD_TYPES.filter(
            ({ key }) => (loneWorkingReportingDashboards[key]?.length ?? 0) > 0
          ).map(({ slug, name }) => {
            const to = reverse(routes.dashboard.alarms.reporting.details, { reportingType: slug });
            return {
              name,
              to,
              isSelected: routing.location.pathname.startsWith(to),
            };
          });

          LoneWorkingSubItems.push({
            name: 'Reporting',
            Icon: <UsageIcon />,
            ActiveIcon: <ActiveUsageIcon />,
            to: reportingSubItems[0]?.to ?? routes.dashboard.alarms.reporting.toString(),
            isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.reporting.toString()),
            subItems: reportingSubItems,
            emptyText: 'No dashboards',
          });
        } else {
          LoneWorkingSubItems.push({
            name: 'Reporting',
            Icon: <UsageIcon />,
            ActiveIcon: <ActiveUsageIcon />,
            to: routes.dashboard.alarms.reporting.toString(),
            isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.reporting.toString()),
          });
        }
      } else {
        LoneWorkingSubItems.push({
          name: 'Usage',
          Icon: <UsageIcon />,
          ActiveIcon: <ActiveUsageIcon />,
          to: routes.dashboard.alarms.reports.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.reports.toString()),
        });
      }
    }

    if (userHasPermissions(SideBarPermissions.LONE_WORKING_ARPS_VISIBLE)) {
      LoneWorkingSubItems.push({
        name: 'Alarm Response Profiles',
        Icon: <ARPIcon />,
        ActiveIcon: <ActiveARPIcon />,
        to: routes.dashboard.alarms.escalationProfiles.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.escalationProfiles.toString()),
      });
    }

    if (userHasPermissions(SideBarPermissions.LONE_WORKING_MAP_VISIBLE, user)) {
      LoneWorkingSubItems.push({
        name: 'Live Map',
        Icon: <MapIcon />,
        ActiveIcon: <ActiveMapIcon />,
        to: routes.dashboard.alarms.activityMap.toString(),
        isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.activityMap.toString()),
      });
    }

    Products.push({
      name: 'Lone Working',
      Icon: <ProtectorIcon />,
      ActiveIcon: <ActiveProtectorIcon />,
      to: routes.dashboard.alarms.red.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.alarms.toString()),
      subItems: LoneWorkingSubItems,
    });
  }

  // Risk Assessments
  if (user && user.activeEntityModules.includes(EntityModules.RisksAssessments)) {
    Products.push({
      name: 'Risk Assessments',
      Icon: <RiskIcon />,
      ActiveIcon: <ActiveRiskIcon />,
      to: routes.dashboard.riskAssessments.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.riskAssessments.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.riskAssessments.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.riskAssessments.toString()),
        },
      ],
    });
  }
  // Risk Register
  if (user && canAccessRisksProduct(user)) {
    Products.push(risksProduct(routing.location.pathname, user));
  } else if (user && user.activeEntityModules.includes(EntityModules.Risks)) {
    Products.push({
      name: 'Risk Register',
      Icon: <RiskIcon />,
      ActiveIcon: <ActiveRiskIcon />,
      to: routes.dashboard.risks.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.risks.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.risks.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.risks.toString()),
        },
      ],
    });
  }
  // Sites
  if (user && user.activeEntityModules.includes(EntityModules.Sites)) {
    Products.push({
      name: 'Sites',
      Icon: <SitesIcon />,
      ActiveIcon: <ActiveSitesIcon />,
      to: routes.dashboard.sites.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.sites.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.sites.toString(),
          isSelected: routing.location.pathname.endsWith(routes.dashboard.sites.toString()),
        },
      ],
    });
  }
  // Assets
  if (user && user.activeEntityModules.includes(EntityModules.Assets)) {
    Products.push({
      name: 'Assets',
      Icon: <AssetsIcon />,
      ActiveIcon: <ActiveAssetsIcon />,
      to: routes.dashboard.assets.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.assets.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.assets.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.assets.toString()),
        },
      ],
    });
  }
  // Contacts
  if (user && user.activeEntityModules.includes(EntityModules.Contacts)) {
    Products.push({
      name: 'Contacts',
      Icon: <ContactsIcon />,
      ActiveIcon: <ActiveContactsIcon />,
      to: routes.dashboard.contacts.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.contacts.toString()),
      subItems: [
        {
          name: 'Overview',
          Icon: <ListIcon />,
          ActiveIcon: <ActiveListIcon />,
          to: routes.dashboard.contacts.toString(),
          isSelected: routing.location.pathname.startsWith(routes.dashboard.contacts.toString()),
        },
      ],
    });
  }
  // Analytics
  if (userHasPermissions(SideBarPermissions.ANALYTICS_VISIBLE, { licenses, user })) {
    const dashboards: SubItemsType = [];
    const analyticIcons = (dashboard: string): JSX.Element => {
      switch (dashboard) {
        case 'audits':
          return <AuditsIcon />;
        case 'protector':
          return <ProtectorIcon />;
        default:
          return <ProtectorIcon />;
      }
    };

    const analyticActiveIcons = (dashboard: string): JSX.Element => {
      switch (dashboard) {
        case 'audits':
          return <AuditsIcon />;
        case 'protector':
          return <ProtectorIcon />;
        default:
          return <ProtectorIcon />;
      }
    };

    const dashboardName = (dashboard: string): string => {
      switch (dashboard) {
        case 'protector':
          return 'Lone Working';
        case 'productAddOns':
          return 'Product Add-ons';
        default:
          return `${dashboard[0].toUpperCase() + dashboard.slice(1).toLowerCase()}`;
      }
    };

    Object.keys(user?.dashboards?.analytics ? user.dashboards.analytics : {}).map(
      (dashboard) =>
        user?.dashboards?.analytics?.[dashboard] &&
        user?.dashboards?.analytics?.[dashboard].length > 0 &&
        dashboards.push({
          name: dashboardName(dashboard),
          to: reverse(routes.dashboard.analytics.details, {
            dashboardId: user?.dashboards?.analytics?.[dashboard][0].uuid || '',
          }),
          isSelected: routing.location.pathname.startsWith(
            reverse(routes.dashboard.analytics.details, {
              dashboardId: user?.dashboards?.analytics?.[dashboard][0].uuid || '',
            })
          ),
          Icon: analyticIcons(dashboard),
          ActiveIcon: analyticActiveIcons(dashboard),
          subItems:
            user?.dashboards?.analytics?.[dashboard]?.map(({ uuid, name }, index) => ({
              uuid,
              name,
              to: reverse(routes.dashboard.analytics.details, {
                dashboardId: user?.dashboards?.analytics?.[dashboard][index].uuid || '',
              }),
              isSelected: routing.location.pathname.startsWith(
                reverse(routes.dashboard.analytics.details, {
                  dashboardId: user?.dashboards?.analytics?.[dashboard][index].uuid || '',
                })
              ),
            })) ?? [],
        })
    );

    Products.push({
      name: 'Analytics',
      Icon: <AnalyticsIcon />,
      ActiveIcon: <ActiveAnalyticsIcon />,
      to: dashboards?.[0]?.subItems?.[0].to || '',
      isSelected: routing.location.pathname.startsWith(routes.dashboard.analytics.toString()),
      subItems: dashboards,
    });
  }

  const canAdmin = !!user && canSeeOrganisationSettings(user);
  const { pathname } = routing.location;

  // Matched exactly so it never co-highlights with Organisation → Users (shared prefix).
  const ownRecordTo = user ? reverse(routes.dashboard.settings.users.details, { userId: user.uuid }) : '';
  const onOwnRecord = !!ownRecordTo && pathname === ownRecordTo;

  const settingsTree: SettingsNavNode[] = [];

  const personalChildren: SettingsNavNode[] = [];
  if (user && user.role !== UserRole.ArcOperator) {
    personalChildren.push({ key: 'profile', label: 'Profile', to: ownRecordTo, isSelected: onOwnRecord });
  }
  if (user && hasNotificationsLicence(user)) {
    personalChildren.push({
      key: 'notifications',
      label: 'Notifications',
      to: routes.dashboard.settings.notifications.toString(),
      isSelected: settingsPathMatches(pathname, routes.dashboard.settings.notifications.toString()),
    });
  }
  personalChildren.push({
    key: 'security',
    label: 'Security',
    to: routes.dashboard.settings.security.toString(),
    isSelected: settingsPathMatches(pathname, routes.dashboard.settings.security.toString()),
  });
  if (user?.timezoneModelEnabled) {
    personalChildren.push({
      key: 'preferences',
      label: 'Preferences',
      to: routes.dashboard.settings.preferences.toString(),
      isSelected: settingsPathMatches(pathname, routes.dashboard.settings.preferences.toString()),
    });
  }
  settingsTree.push({ key: 'personal', label: 'Personal', children: personalChildren });

  const showObjectManager = !!user && user.showObjectManager;

  if (showObjectManager) {
    const objectNodes = CONFIGURABLE_OBJECT_MODULES.filter((module) => user.activeEntityModules.includes(module)).map(
      (module) => buildObjectNode(module, pathname, user)
    );

    if (objectNodes.length > 0) {
      settingsTree.push({ key: 'objects', label: 'Objects', children: objectNodes });
    }
  }
  if (canAdmin || showObjectManager) {
    const organisationChildren: SettingsNavNode[] = [];

    if (canAdmin) {
      organisationChildren.push({
        key: 'users',
        label: 'Users',
        to: routes.dashboard.settings.users.toString(),
        isSelected: settingsPathMatches(pathname, routes.dashboard.settings.users.toString()) && !onOwnRecord,
      });
    }

    if (showObjectManager) {
      const userFieldsTo = routes.dashboard.settings.userFields.toString();
      organisationChildren.push({
        key: 'user-fields',
        label: 'User Fields',
        to: userFieldsTo,
        isSelected: pathname.startsWith(userFieldsTo),
      });
    }

    // Owner-only, matching the API: an admin following this link gets a 403.
    if (user?.role === UserRole.AccountManager && user?.scopedPermissions) {
      const organisationStructureTo = routes.dashboard.settings.organisationStructure.toString();
      organisationChildren.push({
        key: 'organisation-structure',
        label: 'Organisation Structure',
        to: organisationStructureTo,
        isSelected: settingsPathMatches(pathname, organisationStructureTo),
      });
    }

    if (canAdmin && user?.groupsEnabled) {
      const groupsTo = routes.dashboard.settings.groups.toString();
      organisationChildren.push({
        key: 'groups',
        label: 'Groups',
        to: groupsTo,
        isSelected: settingsPathMatches(pathname, groupsTo),
      });
    }

    if (canAdmin) {
      organisationChildren.push(
        {
          key: 'teams',
          label: 'Teams',
          to: routes.dashboard.settings.teams.toString(),
          isSelected: settingsPathMatches(pathname, routes.dashboard.settings.teams.toString()),
        },
        {
          key: 'company-settings',
          label: 'Company settings',
          to: routes.dashboard.settings.companySettings.toString(),
          isSelected: settingsPathMatches(pathname, routes.dashboard.settings.companySettings.toString()),
        },
        {
          key: 'licences',
          label: 'Licences',
          to: routes.dashboard.settings.licenses.toString(),
          isSelected: settingsPathMatches(pathname, routes.dashboard.settings.licenses.toString()),
        }
      );
    }

    settingsTree.push({ key: 'organisation', label: 'Organisation', children: organisationChildren });
  }

  Products.push({
    name: 'Settings',
    hideIcon: true,
    Icon: <SettingsOutlined sx={{ color: colors.grey[400], width: '22px', height: '22px' }} />,
    ActiveIcon: <Settings sx={{ color: colors.common.white, width: '22px', height: '22px' }} />,
    to: user ? resolveDefaultSettingsPath(user) : routes.dashboard.settings.security.toString(),
    isSelected: settingsPathMatches(pathname, routes.dashboard.settings.toString()),
    customContent: <SettingsNavTree nodes={settingsTree} />,
  });

  if (userHasPermissions(SideBarPermissions.MORE_APPS_VISIBLE, licenses)) {
    Products.push({
      name: 'More Apps',
      Icon: <MoreAppsIcon />,
      ActiveIcon: <ActiveMoreAppsIcon />,
      to: routes.dashboard.moreApps.toString(),
      isSelected: routing.location.pathname.startsWith(routes.dashboard.moreApps.toString()),
    });
  }

  return (
    <NewSideBar
      Products={Products}
      current={routing.location.pathname}
      collapsed={!subSideBarOpen}
      hasOrganisationLogo={hasOrganisationLogo}
    />
  );
};

export default observer(SideBar);
